/**
 * GridCellEditor — 按 field.type 渲染内联编辑器
 *
 * 行为：
 *   - 双击单元格 → 进入编辑模式
 *   - Enter / blur → 提交
 *   - Esc → 取消
 *   - enum → 下拉选择, 选中即提交
 *   - boolean → switch toggle, 点击即提交
 */

import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import type { FieldDef } from '@/types/entity';
import { summarizeValue } from '@/lib/format';

// ─── Types ─────────────────────────────────────────

export interface CellEditorProps {
  /** Original value */
  value: unknown;
  /** Field definition from EntityMeta */
  fieldDef: FieldDef;
  /** Called when user commits the edit */
  onCommit: (newValue: unknown) => void;
  /** Called when user cancels (Esc) */
  onCancel: () => void;
}

// ─── Main Component ────────────────────────────────

export function GridCellEditor({ value, fieldDef, onCommit, onCancel }: CellEditorProps) {
  const fieldType = fieldDef.type;

  if (fieldType === 'json' || (value != null && typeof value === 'object')) {
    return (
      <JsonEditor
        value={value}
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  }

  // Enum / select
  if (fieldDef.enumValues && fieldDef.enumValues.length > 0) {
    return (
      <EnumEditor
        value={value}
        options={fieldDef.enumValues}
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  }

  // Boolean
  if (fieldType === 'boolean') {
    return (
      <BooleanEditor
        value={value}
        onCommit={onCommit}
      />
    );
  }

  // Number
  if (fieldType === 'number' || fieldType === 'integer' || fieldType === 'decimal') {
    return (
      <TextInputEditor
        value={value}
        type="number"
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  }

  // Date / Datetime
  if (fieldType === 'date') {
    return (
      <TextInputEditor
        value={value}
        type="date"
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  }

  if (fieldType === 'datetime') {
    return (
      <TextInputEditor
        value={value}
        type="datetime-local"
        onCommit={onCommit}
        onCancel={onCancel}
      />
    );
  }

  // Default: text input
  return (
    <TextInputEditor
      value={value}
      type="text"
      onCommit={onCommit}
      onCancel={onCancel}
    />
  );
}

// ─── Sub-editors ───────────────────────────────────

function TextInputEditor({
  value,
  type,
  onCommit,
  onCancel,
}: {
  value: unknown;
  type: string;
  onCommit: (v: unknown) => void;
  onCancel: () => void;
}) {
  const formatInputValue = (val: unknown): string => {
    if (val == null) return '';
    if (typeof val === 'object') {
      return summarizeValue(val);
    }
    if (type === 'datetime-local' && typeof val === 'string') {
      // Convert ISO to datetime-local format
      try {
        const d = new Date(val);
        return d.toISOString().slice(0, 16);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  const [text, setText] = useState(formatInputValue(value));
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const commit = useCallback(() => {
    if (type === 'number') {
      const num = Number(text);
      if (!isNaN(num)) onCommit(num);
      else onCancel();
    } else {
      onCommit(text);
    }
  }, [text, type, onCommit, onCancel]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <input
      ref={ref}
      type={type}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      className="w-full h-full px-2 py-1 text-sm border-2 border-primary rounded bg-background focus:outline-none"
    />
  );
}

function JsonEditor({
  value,
  onCommit,
  onCancel,
}: {
  value: unknown;
  onCommit: (v: unknown) => void;
  onCancel: () => void;
}) {
  const initialValue =
    value == null
      ? ''
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);
  const [text, setText] = useState(initialValue);
  const [error, setError] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const commit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) {
      onCommit(null);
      return;
    }
    try {
      onCommit(JSON.parse(trimmed));
      setError('');
    } catch {
      setError('Invalid JSON');
      ref.current?.focus();
    }
  }, [text, onCommit]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <div className="space-y-1">
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          if (error) setError('');
        }}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        className="min-h-[120px] w-full rounded border-2 border-primary bg-background px-2 py-1 font-mono text-xs focus:outline-none"
      />
      {error && <p className="text-[10px] text-destructive">{error}</p>}
    </div>
  );
}

function EnumEditor({
  value,
  options,
  onCommit,
  onCancel,
}: {
  value: unknown;
  options: string[];
  onCommit: (v: unknown) => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <select
      ref={ref}
      value={String(value ?? '')}
      onChange={(e) => onCommit(e.target.value)}
      onBlur={onCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
      className="w-full h-full px-1 py-1 text-sm border-2 border-primary rounded bg-background focus:outline-none"
    >
      <option value="">—</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

function BooleanEditor({
  value,
  onCommit,
}: {
  value: unknown;
  onCommit: (v: unknown) => void;
}) {
  const current = Boolean(value);
  // Toggle immediately on render
  useEffect(() => {
    onCommit(!current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null; // No visible editor — toggle is instant
}

// ─── Lock indicator ────────────────────────────────

export function LockIndicator() {
  return (
    <span
      className="inline-flex items-center text-muted-foreground/50"
      title="Field locked — insufficient permissions"
    >
      🔒
    </span>
  );
}

// ─── Editable check helper ─────────────────────────

/**
 * Determine if a cell is editable by the current user.
 * Returns: 'editable' | 'locked' | 'readonly'
 */
export function getCellEditability(
  colEditable: boolean | undefined,
  colEditableBy: string[] | undefined,
  fieldUpdatable: boolean | undefined,
  userRole?: string,
): 'editable' | 'locked' | 'readonly' {
  // If column explicitly says not editable
  const isEditable = colEditable ?? fieldUpdatable ?? false;
  if (!isEditable) return 'readonly';

  // If editableBy is defined, check role
  if (colEditableBy && colEditableBy.length > 0) {
    if (!userRole || !colEditableBy.includes(userRole)) {
      return 'locked';
    }
  }

  return 'editable';
}
